import { useState, useEffect } from "react"
import { Link, useParams } from "react-router-dom"
import axios from "axios"
import { baseUrl } from "../../config/baseUrl"
import empty from "../../assets/images/empty-box.png"
import Swal from "sweetalert2"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    faGraduationCap,
    faPlus,
    faEdit,
    faEye,
    faArrowUp,
    faArrowDown,
    faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"

export default function PackageExams() {
    const { id } = useParams();
    const [exams, setExams] = useState([])
    const [loading, setLoading] = useState(false)
    const [sequenceLoading, setSequenceLoading] = useState({});
    useEffect(() => {
        fetchExamsForPackage()
    }, [id])

    const fetchExamsForPackage = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`${baseUrl}/admin/package/exam/${id}`, {
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            if (response?.data?.status) {
                setExams(response?.data?.data || [])
            } else {
                toast.error(response?.data?.message || "Failed to fetch package exams")
            }
        } catch (error) {
            console.error("Error fetching package exams:", error)
        } finally {
            setLoading(false)
        }
    }

    const handleStatus = async (id) => {
        try {
            const exam = exams.find((e) => e._id === id)
            if (!exam) return

            const newStatus = exam.status === "active" ? "inactive" : "active"

            const response = await axios.patch(
                `${baseUrl}/exams/status/${id}`,
                { status: newStatus },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: localStorage.getItem("token"),
                    },
                },
            )

            if (response?.data?.status) {
                setExams((prevExams) => prevExams.map((exam) => (exam._id === id ? { ...exam, status: newStatus } : exam)))
                toast.success(`Exam ${newStatus === "active" ? "activated" : "deactivated"} successfully`)
            } else {
                toast.error(response?.data?.message || "Failed to update status")
            }
        } catch (error) {
            console.error("Error updating status:", error)
            toast.error("Failed to update status")
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return "N/A"
        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        })
    }
    const updateSequence = async (examId, newSequence) => {
        try {
            setSequenceLoading((prev) => ({ ...prev, [examId]: true }));

            // Find the question that will be swapped
            const otherQuestion = exams.find((q) => q.sequence === newSequence);

            const response = await fetch(
                `${baseUrl}/admin/package/exam/update-sequence`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: localStorage.getItem("token"),
                    },
                    body: JSON.stringify({
                        packageId: id,
                        examId: examId,
                        newSequence: newSequence,
                    }),
                }
            );

            const data = await response.json();

            if (data.status) {
                // Update local state for both swapped questions
                setExams((prevQuestions) => {
                    const updatedQuestions = prevQuestions.map((item) => {
                        if (item._id === examId) {
                            return { ...item, sequence: newSequence };
                        } else if (item._id === otherQuestion?.question?._id) {
                            return {
                                ...item,
                                sequence: prevQuestions.find(
                                    (q) => q._id === examId
                                )?.sequence,
                            };
                        }
                        return item;
                    });
                    return updatedQuestions.sort((a, b) => a.sequence - b.sequence);
                });

                toast.success("Sequence updated successfully");
            } else {
                toast.error(data.message || "Failed to update sequence");
            }

            setSequenceLoading((prev) => ({ ...prev, [examId]: false }));
        } catch (error) {
            console.error("Error updating sequence:", error);
            toast.error("Failed to update sequence");
            setSequenceLoading((prev) => ({ ...prev, [examId]: false }));
        }
    };

    const moveSequenceUp = (examId, currentSequence) => {
        if (currentSequence > 1) {
            updateSequence(examId, currentSequence - 1);
        }
    };

    const moveSequenceDown = (examId, currentSequence) => {
        if (currentSequence < exams.length) {
            updateSequence(examId, currentSequence + 1);
        }
    };

    const handleDelete = async (examId) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`https://api.pidigihub.in/admin/package/exam/delete/`, {
                    headers: {
                        Authorization: localStorage.getItem("token"),
                    },
                    data: {
                        packageId: id,
                        examId,
                    },
                });

                if (response?.data?.status) {
                    setExams((prevExams) => prevExams.filter((exam) => exam._id !== examId));
                    toast.success("Exam deleted successfully");
                    fetchExamsForPackage();
                } else {
                    toast.error(response?.data?.message || "Failed to delete exam");
                }
            } catch (error) {
                console.error("Error deleting exam:", error);
                toast.error("Failed to delete exam");
            }
        }
    };
    return (
        <section className="main-sec">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-lg-6">
                        <div className="dashboard-title">
                            <h4 className="dash-head">
                                <FontAwesomeIcon icon={faGraduationCap} className="me-2" />
                                Package Exams
                            </h4>
                        </div>
                    </div>
                    <div className="col-lg-6 d-flex justify-content-end align-items-start">
                        <Link to="assign-exams" className="btn-add-question">
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Assign Exam
                        </Link>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="cards bus-list">
                            <div className="table-responsive custom-table">
                                <table className="table table-borderless">
                                    <thead>
                                        <tr>
                                            <th>S.No</th>
                                            <th>Sequence</th>
                                            <th>Exam / Year</th>
                                            <th>Duration</th>
                                            <th>Total Score</th>
                                            <th>Created Date</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="loading-spinner">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="mt-2">Loading exams...</p>
                                                </td>
                                            </tr>
                                        ) : exams.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="empty-state">
                                                    <img src={empty || "/placeholder.svg"} alt="No exams" width="200px" />
                                                    <h4>No Exams found!</h4>
                                                </td>
                                            </tr>
                                        ) : (
                                            exams.map((exam, i) => {
                                                const isSequenceLoading = sequenceLoading[exam._id];
                                                return (

                                                    <tr key={exam?._id}>
                                                        <td>{i + 1}</td>
                                                        <td>
                                                            <div className="sequence-controls">
                                                                <span className="sequence-number">
                                                                    {exam.sequence || 0}
                                                                </span>
                                                                <div className="sequence-buttons">
                                                                    <button
                                                                        className="sequence-btn up-btn"
                                                                        onClick={() =>
                                                                            moveSequenceUp(
                                                                                exam._id,
                                                                                exam.sequence
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            exam.sequence === 1 || isSequenceLoading
                                                                        }
                                                                        title="Move Up"
                                                                    >
                                                                        {isSequenceLoading ? (
                                                                            <span
                                                                                className="spinner-border spinner-border-sm"
                                                                                role="status"
                                                                            ></span>
                                                                        ) : (
                                                                            <FontAwesomeIcon icon={faArrowUp} />
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        className="sequence-btn down-btn"
                                                                        onClick={() =>
                                                                            moveSequenceDown(
                                                                                exam._id,
                                                                                exam.sequence
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            exam.sequence === exams.length ||
                                                                            isSequenceLoading
                                                                        }
                                                                        title="Move Down"
                                                                    >
                                                                        {isSequenceLoading ? (
                                                                            <span
                                                                                className="spinner-border spinner-border-sm"
                                                                                role="status"
                                                                            ></span>
                                                                        ) : (
                                                                            <FontAwesomeIcon icon={faArrowDown} />
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="subject-name ">
                                                                {exam?.exam_title || "N/A"}
                                                                <br />
                                                                {exam?.exam_year || "N/A"}
                                                            </div>
                                                        </td>
                                                        <td>{exam?.duration || 0} min</td>
                                                        <td>{exam?.total_score || 0}</td>
                                                        <td>
                                                            <span className="date-text">{formatDate(exam?.createdAt)}</span>
                                                        </td>
                                                        <td>
                                                            <div className="action-buttons">
                                                                <Link to={`/exams/view/${exam?._id}`} className="action-btn view-btn" title="View Exam">
                                                                    <FontAwesomeIcon icon={faEye} />
                                                                </Link>
                                                                <Link to={`/exams/edit/${exam?._id}`} className="action-btn edit-btn" title="Edit Exam">
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                </Link>
                                                                <button
                                                                    className="action-btn delete-btn d-block"
                                                                    title="Delete Exam"
                                                                    onClick={() => handleDelete(exam?._id)}
                                                                    disabled={loading}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                    {/* <span className="tooltip-text">Delete</span> */}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style >{`
                .status-badge {
                    border: none;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                
                .status-active {
                    background-color: #d1e7dd;
                    color: #0f5132;
                }
                
                .status-inactive {
                    background-color: #f8d7da;
                    color: #842029;
                }
                
                .action-buttons {
                    display: flex;
                    gap: 10px;
                }
                
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: none;
                    cursor: pointer;
                    position: relative;
                    transition: all 0.3s ease;
                }
                
                .edit-btn {
                    background-color: #e3f2fd;
                    color: #0d6efd;
                }
                
                .edit-btn:hover {
                    background-color: #0d6efd;
                    color: white;
                }
                
                .delete-btn {
                    background-color: #f8d7da;
                    color: #dc3545;
                }
                
                .delete-btn:hover {
                    background-color: #dc3545;
                    color: white;
                }
                
                .tooltip-text {
                    position: absolute;
                    top: -30px;
                    left: 50%;
                    transform: translateX(-50%);
                    background-color: #333;
                    color: white;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    white-space: nowrap;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s ease;
                }
                
                .action-btn:hover .tooltip-text {
                    opacity: 1;
                    visibility: visible;
                }
                
                .empty-state {
                    text-align: center;
                    padding: 40px 20px;
                }
                
                .empty-state img {
                    margin-bottom: 20px;
                }
                
                .empty-state h4 {
                    margin-bottom: 10px;
                    color: #333;
                }
                
                .spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid rgba(0, 0, 0, 0.1);
                    border-radius: 50%;
                    border-top: 4px solid #667eea;
                    animation: spin 1s linear infinite;
                    margin: 40px auto;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .btn-add-question {
                    background: #008080;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 5px;
                    font-weight: 500;
                    display: inline-flex;
                    align-items: center;
                    text-decoration: none;
                    transition: all 0.3s ease;
                }
                
                .btn-add-question:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
                    color: white;
                }
            `}</style>
            <style>
                {`
                .subject-badge {
                    background-color: #e3f2fd;
                    color: #1976d2;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 0.8rem;
                    font-weight: 500;
                }

                .sequence-controls {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .sequence-number {
                    font-weight: bold;
                    color: #495057;
                    min-width: 20px;
                    text-align: center;
                }

                .sequence-buttons {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .sequence-btn {
                    background: none;
                    border: 1px solid #dee2e6;
                    padding: 2px 6px;
                    border-radius: 3px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    font-size: 0.7rem;
                    width: 24px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .sequence-btn:hover:not(:disabled) {
                    background-color: #f8f9fa;
                    border-color: #adb5bd;
                }

                .sequence-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .up-btn {
                    color: #28a745;
                }

                .down-btn {
                    color: #dc3545;
                }

                .up-btn:hover:not(:disabled) {
                    background-color: rgba(40, 167, 69, 0.1);
                    border-color: #28a745;
                }

                .down-btn:hover:not(:disabled) {
                    background-color: rgba(220, 53, 69, 0.1);
                    border-color: #dc3545;
                }

                .btn-add-question {
                    background: linear-gradient(135deg, #007bff, #0056b3);
                    color: white;
                    text-decoration: none;
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    border: none;
                    display: inline-flex;
                    align-items: center;
                }

                .btn-add-question:hover {
                    color: white;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
                    text-decoration: none;
                }

                .loading-spinner {
                    text-align: center;
                    padding: 40px;
                }

                .empty-state {
                    text-align: center;
                    padding: 40px;
                }

                .search-container .search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #6c757d;
                    z-index: 10;
                }

                .search-input {
                    padding-left: 40px !important;
                }

                .action-btn {
                    background: none;
                    border: none;
                    padding: 6px 8px;
                    margin: 0 2px;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .edit-btn {
                    color: #007bff;
                    text-decoration: none;
                }

                .edit-btn:hover {
                    background-color: rgba(0, 123, 255, 0.1);
                    color: #0056b3;
                }

                .delete-btn {
                    color: #dc3545;
                }

                .delete-btn:hover {
                    background-color: rgba(220, 53, 69, 0.1);
                    color: #c82333;
                }

                .status-active {
                    background-color: #28a745;
                    color: white;
                    border: none;
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 0.8rem;
                }

                .status-inactive {
                    background-color: #6c757d;
                    color: white;
                    border: none;
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 0.8rem;
                }

                .pagination-container {
                    margin-top: 20px;
                }

                .page-link {
                    color: #007bff;
                    background-color: #fff;
                    border: 1px solid #dee2e6;
                }

                .page-item.active .page-link {
                    background-color: #007bff;
                    border-color: #007bff;
                    color: white;
                }

                .page-link:hover {
                    color: #0056b3;
                    background-color: #e9ecef;
                    border-color: #dee2e6;
                }
                    .btn-add-question{
                      font-size:12px;
                    }
                `}
            </style>
        </section>
    )
}
